import {useMutation, useQuery} from '@apollo/client'
import React, {useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {CREATE_PIPELINE, UPDATE_PIPELINE} from '../../../gql/mutations/pipelineMutations'
import {IResAddPipe, PipelineMutationPayload} from '../../../types/pipelines'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import {GET_ALL_ORGANIZATIONS} from '../../../gql/queries/organizationsQuery'
import {useGetUser} from '../../../store/userStore'

interface IModalAddPipelines {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoEdit?: null | {id: string; name: string; organization?: {id: string; name: string}}
}

const apM = defineMessages({
  addTitle: {id: 'leads.pipelines.addTitle', defaultMessage: 'Add pipeline'},
  editTitle: {id: 'leads.pipelines.editTitle', defaultMessage: 'Edit pipeline'},
  nameLabel: {id: 'leads.pipelines.nameLabel', defaultMessage: 'Pipeline name'},
  enterName: {id: 'leads.pipelines.enterName', defaultMessage: 'Enter name'},
  orgLabel: {id: 'leads.pipelines.thOrganization', defaultMessage: 'Organization'},
  orgHint: {id: 'leads.pipelines.orgHint', defaultMessage: 'Organization is set to your current organization. Only SuperAdministrador can change this.'},
  toastAdded: {id: 'leads.pipelines.toast.added', defaultMessage: 'Pipeline added successfully'},
  toastEdited: {id: 'leads.pipelines.toast.edited', defaultMessage: 'Pipeline edited successfully'},
})

export const ModalAddPipeline: React.FC<IModalAddPipelines> = ({
  isOpen,
  onClose,
  refetch,
  infoEdit,
}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})

  const mutationDoc = infoEdit ? UPDATE_PIPELINE : CREATE_PIPELINE
  const [gqlAddEditPipeline, {data, loading, error}] = useMutation<IResAddPipe>(mutationDoc)
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')

  // Get current user info
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')

  // Normalize ID helper
  const normalizeId = (id?: string | number | null) => (id ? `${parseId(id)}` : '')

  // Query for organizations
  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
  }, [error])

  // Sync default org selection when modal opens or data changes
  useEffect(() => {
    // Prefer the organization from the pipeline being edited
    if (infoEdit?.organization?.id) {
      setSelectedOrgId(normalizeId(infoEdit.organization.id))
      return
    }

    // For non superadmin, force current user's org
    if (!isSuperAdministrador && currentUser?.organization?.id) {
      setSelectedOrgId(normalizeId(currentUser.organization.id))
      return
    }

    // Otherwise empty
    setSelectedOrgId('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoEdit, isSuperAdministrador, currentUser?.organization?.id, isOpen])

  useEffect(() => {
    if (!data) return
    const payload: PipelineMutationPayload | undefined =
      data.updatePipelineMutation || data.createPipelineMutation

    if (payload?.pipeline?.id) {
      toast.success(intl.formatMessage(infoEdit ? apM.toastEdited : apM.toastAdded))
      onClose()
      refetch()
    } else if (payload?.errors?.length) {
      toast.error(errMsg(payload.errors?.[0]?.message || payload.errors?.[0]?.messages?.[0] || intl.formatMessage({id: 'leads.add.toast.somethingHappened', defaultMessage: 'something happened'})))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const {name} = Object.fromEntries(new FormData(e.currentTarget))
    const orgIdValue = selectedOrgId ? parseId(selectedOrgId) : undefined

    if (infoEdit) {
      gqlAddEditPipeline({
        variables: {
          pipelineId: parseId(infoEdit.id),
          pipelineData: {
            name,
            organizationId: orgIdValue,
          },
        },
      })
    } else {
      gqlAddEditPipeline({
        variables: {
          pipelineData: {
            name,
            organizationId: orgIdValue,
          },
        },
      })
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(infoEdit ? apM.editTitle : apM.addTitle)}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSubmit}>
        <Modal.Body>
          <Form.Group className='mb-3' controlId='exampleForm.ControlInput1'>
            <Form.Label>{intl.formatMessage(apM.nameLabel)}</Form.Label>
            <Form.Control
              type='name'
              name='name'
              defaultValue={infoEdit ? infoEdit.name : ''}
              required
              placeholder={intl.formatMessage(apM.enterName)}
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>{intl.formatMessage(apM.orgLabel)}</Form.Label>
            <Form.Select
              name='organizationId'
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              disabled={!isSuperAdministrador || loadingOrganizations}
            >
              {loadingOrganizations ? (
                <option value=''>{intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading...'})}</option>
              ) : (
                <option value=''>{intl.formatMessage({id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'})}</option>
              )}
              {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => {
                const value = normalizeId(node.id)
                return (
                  <option key={node.id} value={value}>
                    {node.name}
                  </option>
                )
              })}
            </Form.Select>
            {!isSuperAdministrador && (
              <Form.Text className='text-muted'>
                {intl.formatMessage(apM.orgHint)}
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
          </Button>
          <Button
            type='submit'
            variant='primary'
            className='d-flex align-items-center'
            disabled={loading}
          >
            {loading && <Spinner animation='border' size='sm' />}
            <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
